import store from '../../src/state/store'
import { delay } from 'lodash'

export default [
    {
        path: '/',
        name: 'landing',
        component: () => lazyLoadView(import('@/src/router/views/landing')),
        meta: {
            // If the user is already logged in
            // and route has no params with extra workflow
            // redirect to the home page instead
            // else continue to the landing/register page
            beforeResolve(routeTo, routeFrom, next) {
                const {workflow} = routeTo.params
                const forceLogin = workflow === 'invite'
                !forceLogin && store.getters['auth/loggedIn'] ? next({name: 'home'}) : next({params: routeTo.params})
            }
        }
    },
    {
        path: '/home',
        name: 'home',
        component: () => lazyLoadView(import('@/src/router/views/home')),
        meta: {
            authRequired: true
        }
    },
    {
        path: '/templates',
        name: 'templates',
        component: () => lazyLoadView(import('@/src/router/views/templates')),
        meta: {
            authRequired: true,
            groups: ['Administrator', 'Coach']
        }
    },
    {
        path: '/coachings',
        name: 'coachings',
        component: () => lazyLoadView(import('@/src/router/views/coachings')),
        meta: {
            authRequired: true
        }
    },
    {
        path: '/coaching',
        name: 'coaching',
        component: () => lazyLoadView(import('@/src/router/views/coaching')),
        meta: {
            authRequired: true,
            groups: ['Administrator', 'Coach'],
            beforeResolve(routeTo, routeFrom, next) {
                // prevent fetching all data for current organisation
                // TODO move from api.fetching to app store
                void store.commit('api/SET_FETCHING', {disabled: true})
                delay(next, 1500)
            }
        }
    },
    {
        path: '/organizations',
        name: 'organizations',
        component: () => lazyLoadView(import('@/src/router/views/organisations')),
        meta: {
            authRequired: true
        }
    },
    {
        path: '/employees',
        name: 'employees',
        component: () => lazyLoadView(import('@/src/router/views/agents')),
        meta: {
            authRequired: true,
            groups: ['Administrator', 'Coach']
        }
    },
    {
        path: '/reporting',
        name: 'reporting',
        component: () => lazyLoadView(import('@/src/router/views/reporting')),
        meta: {
            authRequired: true
        }
    },
    {
        path: '/login',
        name: 'login',
        component: () => lazyLoadView(import('@/src/router/views/login')),
        meta: {
            // If the user is already logged in
            // and route has no params with extra workflow
            // redirect to the home page instead
            // else continue to the login page
            beforeResolve(routeTo, routeFrom, next) {
                const {workflow, token} = routeTo.params
                const forceLogin = workflow === 'invite' && !!token
                !forceLogin && store.getters['auth/loggedIn'] ? next({name: 'home'}) : next({params: routeTo.params})
            }
        }
    },
    {
        path: '/logout',
        name: 'logout',
        meta: {
            authRequired: true,
            beforeResolve(routeTo, routeFrom, next) {
                void store.dispatch('auth/logOut')
                const authRequiredOnPreviousRoute = routeFrom.matched.some(route => route.meta.authRequired)
                // Navigate back to previous page, or home as a fallback
                next(authRequiredOnPreviousRoute ? {name: 'landing'} : {...routeFrom})
            }
        }
    },
    {
        path: '/verifyemail',
        name: 'verifyemail',
        component: () => lazyLoadView(import('@/src/router/views/verifyemail'))
    },
    {
        path: '/resetpassword',
        name: 'resetpassword',
        component: () => lazyLoadView(import('@/src/router/views/resetpassword'))
    },
    {
        path: '/register',
        name: 'register',
        meta: {
            beforeResolve(routeTo, routeFrom, next) {
                const {invitee} = routeTo.query
                const params = {workflow: 'invite', invitee}
                next({name: 'landing', params})
            }
        }
    },
    {
        path: '/administration',
        name: 'administration',
        component: () => lazyLoadView(import('@/src/router/views/administration')),
        meta: {
            authRequired: true,
            // If the user is now allowed
            // redirect to the home page
            beforeResolve(routeTo, routeFrom, next) {
                !store.getters['auth/developerMode'] ? next({name: 'home'}) : next()
            }
        }
    },
    {
        path: '/404',
        redirect: '/login'
        // name: '404',
        // component: require('@views/_404').default,
        // // Allows props to be passed to the 404 page through route
        // // params, such as `resource` to define what wasn't found.
        // props: true
    },
    // Redirect any unmatched routes to the 404 page. This may
    // require some server configuration to work in production:
    // https://router.vuejs.org/en/essentials/history-mode.html#example-server-configurations
    {
        path: '*',
        redirect: '/login'
    }
]

// Lazy-loads view components, but with better UX. A loading view
// will be used if the component takes a while to load, falling
// back to a timeout view in case the page fails to load. You can
// use this component to lazy-load a route with:
//
// component: () => lazyLoadView(import('@views/my-view'))
//
// NOTE: Components loaded with this strategy DO NOT have access
//  to in-component guards, such as beforeRouteEnter,
//  beforeRouteUpdate, and beforeRouteLeave. You must either use
//  route-level guards instead or lazy-load the component directly:
//
// component: () => import('@views/my-view')
//
function lazyLoadView(AsyncView) {
    const AsyncHandler = () => ({
        component: AsyncView,
        // A component to use while the component is loading.
        // loading: require('@/src/router/views/_loading').default,
        // Delay before showing the loading component.
        // Default: 200 (milliseconds).
        delay: 400,
        // A fallback component in case the timeout is exceeded
        // when loading the component.
        error: require('@/src/router/views/_timeout').default,
        // Time before giving up trying to load the component.
        // Default: Infinity (milliseconds).
        timeout: 10000
    })

    return Promise.resolve({
        functional: true,
        render(h, {data, children}) {
            // Transparently pass any props or children
            // to the view component.
            return h(AsyncHandler, data, children)
        }
    })
}
